import React, { Suspense, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';

import { HomeBaseDrawerWrapper } from 'app/components/CommonStyled/HomeDrawers/HomeBaseDrawer/styled';
import PersonAvatar from 'app/components/CommonStyled/PersonAvatar';
import { EmailIcon } from 'app/components/shared/icons';
import { AnimatePresence, motion } from 'framer-motion';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import useUserData from 'hooks/useUserData';
import { ICollectiveDirectoryPersonDrawerResponse } from 'interfaces';
import {
  IPeopleContext,
  PEOPLE_CONTEXT,
  setSelectedPeople,
  setTeamingContactDrawer,
} from 'store/reducers/people/people-reducer';
import { ThemeProvider } from 'styled-components';
import { getCompanyLogoUrl } from 'utils';

import {
  CollectiveDirectoryPersonDrawerCTAButton,
  CollectiveDirectoryPersonDrawerCTAButtonsContainer,
  CollectiveDirectoryPersonDrawerCTAButtonText,
  CollectiveDirectoryPersonDrawerCTAWrapper,
} from './styled';

const TeamingShareList = React.lazy(() => import('app/components/Teaming/TeamingShareList'));
const ContactDrawer = React.lazy(() => import('app/components/Drawers/Contact'));

interface CollectiveDirectoryPersonDrawerCTAProps {
  person: ICollectiveDirectoryPersonDrawerResponse;
  context: IPeopleContext;
  disabled?: boolean;
}

export default function CollectiveDirectoryPersonDrawerCTA({
  person,
  context,
  disabled = false,
}: CollectiveDirectoryPersonDrawerCTAProps) {
  return (
    <ThemeProvider theme={{ context }}>
      {context === PEOPLE_CONTEXT.TEAMING ? (
        person.info.is_freelancer ? (
          <CollectiveDirectoryPersonDrawerCTA.TeamingFreelancer person={person} />
        ) : (
          <CollectiveDirectoryPersonDrawerCTA.Teaming person={person} disabled={disabled} />
        )
      ) : (
        <CollectiveDirectoryPersonDrawerCTA.CollectiveDirectory person={person} />
      )}
    </ThemeProvider>
  );
}

CollectiveDirectoryPersonDrawerCTA.Teaming = function CollectiveDirectoryPersonDrawerCTATeaming({
  person,
  disabled = false,
}: {
  person: ICollectiveDirectoryPersonDrawerResponse;
  disabled?: boolean;
}) {
  const dispatch = useDispatch();
  const userData = useUserData();
  const [showShareListDrawer, setShowListDrawer] = useState<boolean>(false);
  const teamingDrawerSendMessageBehavioralFunction = useBehavioral(
    BEHAVIORALS.TEAMING_DRAWER_SEND_MESSAGE
  );

  const handleShareListClick = () => {
    setShowListDrawer(true);
  };
  const handleShareListDrawerClose = () => {
    setShowListDrawer(false);
  };

  const isTeamable = useMemo(() => {
    return person.info.cross_kyu_teaming || person.info.company_slug === userData.kyu_company.slug;
  }, [person, userData]);

  const selectedPeople = useMemo(() => {
    return [person.info];
  }, []);

  const handleContactClick = () => {
    if (!isTeamable) return;
    teamingDrawerSendMessageBehavioralFunction({
      related_object_uuid: person.info.uuid,
    });
    dispatch(setTeamingContactDrawer({ show: true, person: person.info }));
  };

  return (
    <CollectiveDirectoryPersonDrawerCTAWrapper>
      <CollectiveDirectoryPersonDrawerCTAButtonsContainer>
        <CollectiveDirectoryPersonDrawerCTAButton
          disabled={!isTeamable || disabled}
          onClick={handleContactClick}
        >
          <EmailIcon width={16} height={16} fill="var(--color-primary)" />
          <CollectiveDirectoryPersonDrawerCTAButtonText>
            SEND MESSAGE
          </CollectiveDirectoryPersonDrawerCTAButtonText>
        </CollectiveDirectoryPersonDrawerCTAButton>
      </CollectiveDirectoryPersonDrawerCTAButtonsContainer>
      {ReactDOM.createPortal(
        <AnimatePresence>
          {showShareListDrawer ? (
            <motion.div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                x: '100%',
                zIndex: 9999,
              }}
              animate={{
                x: 0,
              }}
              transition={{
                duration: 0.45,
              }}
              exit={{
                x: '100%',
              }}
            >
              <Suspense fallback={<HomeBaseDrawerWrapper />}>
                <TeamingShareList
                  people={selectedPeople}
                  showCloseButton
                  onClose={handleShareListDrawerClose}
                />
              </Suspense>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.querySelector('[data-drawer="teaming-contact"]') ||
          document.querySelector('[data-type="sidedrawer"]') ||
          document.getElementById('root')
      )}
    </CollectiveDirectoryPersonDrawerCTAWrapper>
  );
};

CollectiveDirectoryPersonDrawerCTA.TeamingFreelancer =
  function CollectiveDirectoryPersonDrawerCTATeamingFreelancer({
    person,
  }: {
    person: ICollectiveDirectoryPersonDrawerResponse;
  }) {
    const [showShareListDrawer, setShowListDrawer] = useState<boolean>(false);
    const userData = useUserData();
    const dispatch = useDispatch();
    const teamingDrawerSendMessageBehavioralFunction = useBehavioral(
      BEHAVIORALS.TEAMING_DRAWER_SEND_MESSAGE
    );
    const isTeamable = useMemo(() => {
      return (
        person.info.cross_kyu_teaming || person.info.company_slug === userData.kyu_company.slug
      );
    }, [person, userData]);

    const selectedPeople = useMemo(() => {
      return [person.info];
    }, []);

    const handleContactClick = () => {
      if (!isTeamable) return;
      teamingDrawerSendMessageBehavioralFunction({
        related_object_uuid: person.info.uuid,
      });
      dispatch(setSelectedPeople([person.info]));
      dispatch(setTeamingContactDrawer({ show: true, person: person.info }));
    };

    const handleShareListDrawerClose = () => {
      setShowListDrawer(false);
    };

    return (
      <CollectiveDirectoryPersonDrawerCTAWrapper>
        <CollectiveDirectoryPersonDrawerCTAButtonsContainer>
          <CollectiveDirectoryPersonDrawerCTAButton onClick={handleContactClick}>
            <EmailIcon width={16} height={16} fill="var(--color-primary)" />
            <CollectiveDirectoryPersonDrawerCTAButtonText>
              SEND MESSAGE
            </CollectiveDirectoryPersonDrawerCTAButtonText>
          </CollectiveDirectoryPersonDrawerCTAButton>
        </CollectiveDirectoryPersonDrawerCTAButtonsContainer>
        {ReactDOM.createPortal(
          <AnimatePresence>
            {showShareListDrawer ? (
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  x: '100%',
                  zIndex: 9999,
                }}
                animate={{
                  x: 0,
                }}
                transition={{
                  duration: 0.45,
                }}
                exit={{
                  x: '100%',
                }}
              >
                <Suspense fallback={<HomeBaseDrawerWrapper />}>
                  <TeamingShareList
                    people={selectedPeople}
                    showCloseButton
                    onClose={handleShareListDrawerClose}
                  />
                </Suspense>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.querySelector('[data-drawer="teaming-contact"]') ||
            document.querySelector('[data-type="sidedrawer"]') ||
            document.getElementById('root')
        )}
      </CollectiveDirectoryPersonDrawerCTAWrapper>
    );
  };

CollectiveDirectoryPersonDrawerCTA.CollectiveDirectory =
  function CollectiveDirectoryPersonDrawerCTACollectiveDirectory({
    person,
  }: {
    person: ICollectiveDirectoryPersonDrawerResponse;
  }) {
    const [showShareListDrawer, setShowListDrawer] = useState<boolean>(false);
    const [showContactDrawer, setShowContactDrawer] = useState<boolean>(false);
    const teamingDrawerSendMessageBehavioralFunction = useBehavioral(
      BEHAVIORALS.TEAMING_DRAWER_SEND_MESSAGE
    );

    const handleContactClick = () => {
      teamingDrawerSendMessageBehavioralFunction({
        related_object_uuid: person.info.uuid,
      });
      setShowContactDrawer(true);
    };

    const handleContactDrawerClose = () => {
      setShowContactDrawer(false);
    };

    const handleShareListDrawerClose = () => {
      setShowListDrawer(false);
    };

    return (
      <CollectiveDirectoryPersonDrawerCTAWrapper>
        <CollectiveDirectoryPersonDrawerCTAButtonsContainer>
          <CollectiveDirectoryPersonDrawerCTAButton onClick={handleContactClick}>
            <EmailIcon width={16} height={16} fill="var(--color-primary)" />
            <CollectiveDirectoryPersonDrawerCTAButtonText>
              SEND MESSAGE
            </CollectiveDirectoryPersonDrawerCTAButtonText>
          </CollectiveDirectoryPersonDrawerCTAButton>
        </CollectiveDirectoryPersonDrawerCTAButtonsContainer>
        {ReactDOM.createPortal(
          <AnimatePresence>
            {showContactDrawer ? (
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  x: '100%',
                  zIndex: 99999999999,
                }}
                animate={{
                  x: 0,
                }}
                transition={{
                  duration: 0.45,
                }}
                exit={{
                  x: '100%',
                }}
              >
                <Suspense fallback={<HomeBaseDrawerWrapper />}>
                  <ContactDrawer
                    backToRecipient={handleContactDrawerClose}
                    selectedUuids={[person.info.uuid]}
                    initialStep={2}
                  />
                </Suspense>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.querySelector('[data-drawer="teaming-contact"]') ||
            document.querySelector('[data-type="sidedrawer"]') ||
            document.getElementById('root')
        )}
        {ReactDOM.createPortal(
          <AnimatePresence>
            {showShareListDrawer ? (
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  x: '100%',
                  zIndex: 9999,
                }}
                animate={{
                  x: 0,
                }}
                transition={{
                  duration: 0.45,
                }}
                exit={{
                  x: '100%',
                }}
              >
                <Suspense fallback={<HomeBaseDrawerWrapper />}>
                  <TeamingShareList
                    people={[person.info]}
                    showCloseButton
                    onClose={handleShareListDrawerClose}
                  />
                </Suspense>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.querySelector('[data-drawer="teaming-contact"]') ||
            document.querySelector('[data-type="sidedrawer"]') ||
            document.getElementById('root')
        )}
      </CollectiveDirectoryPersonDrawerCTAWrapper>
    );
  };
